import React, { createContext, useContext, useState, useCallback } from "react";

const DataContext = createContext(null);

export const ADMIN_TABLE = "tbl75X9z2yc5VqRW";
export const USER_TABLE  = "tblRZFA1B3NL7IOT";

const WORKER_URL = "https://task-system.aquint-automation.workers.dev";

async function apiFetch(path, options, token) {
  const res = await fetch(`${WORKER_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({ error: res.statusText }));
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

export function DataProvider({ children }) {
  const [adminRecords, setAdminRecords] = useState([]);
  const [userRecords,  setUserRecords]  = useState([]);
  const [larkUsers,    setLarkUsers]    = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [lastFetched,  setLastFetched]  = useState(null);

  const getToken = () => localStorage.getItem("tms_token");

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); 
    setError(null);
    try {
      const t = getToken();
      if (!t) return;

      const fetchList = [
        apiFetch(`/api/records/${ADMIN_TABLE}`, {}, t),
        apiFetch(`/api/records/${USER_TABLE}`,  {}, t),
        apiFetch(`/api/users`, {}, t) // Always fetch users to keep it fresh
      ];
      
      const [a, u, users] = await Promise.all(fetchList);
      
      setAdminRecords(a.records || []);
      setUserRecords(u.records  || []);
      if (users) setLarkUsers(users.users  || []);
      setLastFetched(new Date().toLocaleTimeString());
    } catch (e) { setError(e.message); }
    finally     { if (!silent) setLoading(false); }
  }, []);

  const createTask = useCallback(async (fields) => {
    const data = await apiFetch(`/api/records/${ADMIN_TABLE}`, {
      method: "POST",
      body: JSON.stringify({ fields }),
    }, getToken());
    
    // Optimistic Update: Add new record to state immediately
    if (data.record) {
      setAdminRecords(prev => [data.record, ...prev]);
    }
    
    // Background refresh to sync associated user tasks
    refresh(true); 
    return data;
  }, [refresh]);

  const updateTask = useCallback(async (tableId, recordId, fields) => {
    const data = await apiFetch(`/api/records/${tableId}/${recordId}`, {
      method: "PATCH",
      body: JSON.stringify({ fields }),
    }, getToken());
    
    // Optimistic Update: Replace updated record in state
    if (data.record) {
      const updater = prev => prev.map(r => r.record_id === recordId ? data.record : r);
      if (tableId === ADMIN_TABLE) setAdminRecords(updater);
      else setUserRecords(updater);
    }
    
    // Background refresh for consistency (delay to allow Lark to settle)
    setTimeout(() => refresh(true), 1500);
    return data;
  }, [refresh]);

  const deleteTask = useCallback(async (tableId, recordId) => {
    const data = await apiFetch(`/api/records/${tableId}/${recordId}`, {
      method: "DELETE",
    }, getToken());
    
    // Optimistic Update: Remove from state immediately
    const filterer = prev => prev.filter(r => r.record_id !== recordId);
    if (tableId === ADMIN_TABLE) setAdminRecords(filterer);
    else setUserRecords(filterer);
    
    // Background refresh
    refresh(true);
    return data;
  }, [refresh]);

  const runCron = useCallback(async () => {
    return apiFetch("/api/cron/run", { method: "POST" }, getToken());
  }, []);

  const getAttachments = useCallback(async (taskId) => {
    const data = await apiFetch(`/api/attachments/${taskId}`, {}, getToken());
    return data.attachments || [];
  }, []);

  const deleteAttachment = useCallback(async (taskId, fileToken) => {
    return apiFetch(`/api/attachments/${taskId}/${fileToken}`, {
      method: "DELETE",
    }, getToken());
  }, []);

  const getComments = useCallback(async (taskId) => {
    const data = await apiFetch(`/api/comments/${taskId}`, {}, getToken());
    return data.comments || [];
  }, []);

  const addComment = useCallback(async (taskId, text) => {
    return apiFetch(`/api/comments/${taskId}`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }, getToken());
  }, []);

  const uploadFile = useCallback(async (file, taskId) => {
    const token = getToken();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("taskId", taskId || "");
    const res = await fetch(`${WORKER_URL}/api/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data;
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      const t = getToken();
      const users = await apiFetch(`/api/users?refresh=true`, {}, t);
      if (users) setLarkUsers(users.users || []);
    } catch (e) { console.error("User refresh failed:", e.message); }
  }, []);

  return (
    <DataContext.Provider value={{
      adminRecords, userRecords, larkUsers,
      loading, error, lastFetched,
      refresh, createTask, updateTask, deleteTask, runCron, getComments, addComment, uploadFile, getAttachments, deleteAttachment, refreshUsers,
      ADMIN_TABLE, USER_TABLE,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
